// Approval workflow hook — submit, approve, reject, request changes
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'

const APPROVAL_KEY = 'approval'

interface SubmitParams {
  issueId: string
  resolutionMethodId: string
  minValidators?: number
  autoApproveEligible?: boolean
  validatorIds?: string[]
}

export function useApproval(issueId: string | undefined) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const validationsQuery = useQuery({
    queryKey: [APPROVAL_KEY, issueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('solution_validations')
        .select('*')
        .eq('issue_id', issueId!)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data ?? []
    },
    enabled: !!issueId && !!user,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [APPROVAL_KEY, issueId] })

  const submitMutation = useMutation({
    mutationFn: async (params: SubmitParams) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const response = await fetch(`${supabaseUrl}/functions/v1/solution-validator`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'submit', ...params }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Submit failed' }))
        throw new Error(err.error || 'Submit failed')
      }
      return response.json()
    },
    onSuccess: invalidate,
  })

  const approveMutation = useMutation({
    mutationFn: async (validationId: string) => {
      const { error } = await supabase
        .from('solution_validations')
        .update({ status: 'approved', validated_by: user?.id, validated_at: new Date().toISOString() } as never)
        .eq('id', validationId)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const rejectMutation = useMutation({
    mutationFn: async ({ validationId, reason }: { validationId: string; reason: string }) => {
      const { error } = await supabase
        .from('solution_validations')
        .update({ status: 'rejected', rejection_reason: reason, validated_by: user?.id, validated_at: new Date().toISOString() } as never)
        .eq('id', validationId)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  const requestChangesMutation = useMutation({
    mutationFn: async ({ validationId, changes }: { validationId: string; changes: string }) => {
      const { error } = await supabase
        .from('solution_validations')
        .update({ status: 'changes_requested', change_requests: changes, validated_by: user?.id } as never)
        .eq('id', validationId)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  return {
    validations: validationsQuery.data ?? [],
    isLoading: validationsQuery.isLoading,
    submitForApproval: submitMutation.mutateAsync,
    isSubmitting: submitMutation.isPending,
    approve: approveMutation.mutateAsync,
    reject: rejectMutation.mutateAsync,
    requestChanges: requestChangesMutation.mutateAsync,
  }
}
